import React, { useState, useEffect } from 'react'
import { Mic, Download, Loader2, AlertCircle, Plus, GripVertical } from 'lucide-react'
import { voiceApi } from '../api/client'
import { VoiceProfileCard } from './VoiceProfileCard'

interface VoiceHistoryItem {
  job_id: string
  text: string
  status: 'pending' | 'ready' | 'failed'
  download_url?: string
  error?: string
  duration?: number
  created: Date
}

interface VoicePanelProps {
  /** Add a finished voiceover to the timeline's Voice track. */
  onAddToTimeline?: (clip: { url: string; text: string; duration?: number }) => void
}

const POLL_INTERVAL_MS = 3000
const MAX_TEXT_LENGTH = 5000
const HISTORY_KEY = 'legit2clip_voice_history'

export function VoicePanel({ onAddToTimeline }: VoicePanelProps = {}) {
  const [text, setText] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [statusText, setStatusText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [history, setHistory] = useState<VoiceHistoryItem[]>([])
  const [hasVoice, setHasVoice] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // Restore this session's generations so they survive closing/reopening the
  // Create panel (the panel unmounts, but the work — and the clips — persist).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY)
      if (raw) {
        const parsed: VoiceHistoryItem[] = JSON.parse(raw).map((h: any) => ({ ...h, created: new Date(h.created) }))
        setHistory(parsed)
        const stillPending = parsed.find(h => h.status === 'pending')
        if (stillPending) setActiveJobId(stillPending.job_id) // resume polling an in-flight job
      }
    } catch {}
    setLoaded(true)
  }, [])

  useEffect(() => {
    if (!loaded) return
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history)) } catch {}
  }, [history, loaded])

  useEffect(() => {
    if (!activeJobId) return
    let cancelled = false
    setIsGenerating(true)

    const poll = async () => {
      try {
        const data = await voiceApi.getStatus(activeJobId)
        if (cancelled) return
        if (data.ready) {
          const url = voiceApi.getDownloadUrl(activeJobId)
          setHistory(prev => prev.map(item => item.job_id === activeJobId ? { ...item, status: 'ready', download_url: url } : item))
          setIsGenerating(false); setActiveJobId(null); setStatusText('')
        } else if (data.status === 'failed' || data.status === 'cancelled') {
          const err = data.error_message || 'Generation failed'
          setHistory(prev => prev.map(item => item.job_id === activeJobId ? { ...item, status: 'failed', error: err } : item))
          setErrorMessage(err); setIsGenerating(false); setActiveJobId(null); setStatusText('')
        } else {
          const pct = typeof data.progress === 'number' ? ` — ${Math.round(data.progress)}%` : ''
          setStatusText(`${data.status || 'processing'}${pct}`)
        }
      } catch {
        // transient — keep polling
      }
    }

    poll()
    const interval = window.setInterval(poll, POLL_INTERVAL_MS)
    return () => { cancelled = true; window.clearInterval(interval) }
  }, [activeJobId])

  const handleGenerate = async () => {
    const prompt = text.trim()
    if (!prompt || isGenerating) return
    setErrorMessage(''); setIsGenerating(true); setStatusText('Submitting...')
    try {
      const data = await voiceApi.generate({ text: prompt })
      if (!data.job_id) throw new Error('No job id returned')
      setHistory(prev => [{ job_id: data.job_id, text: prompt, status: 'pending' as const, created: new Date() }, ...prev])
      setActiveJobId(data.job_id)
      setStatusText('Queued...')
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || error?.message || 'Failed to start generation')
      setIsGenerating(false); setStatusText('')
    }
  }

  const setDuration = (jobId: string, d: number) => {
    if (!d || !isFinite(d)) return
    setHistory(prev => prev.map(item => item.job_id === jobId ? { ...item, duration: d } : item))
  }

  const addToTimeline = (item: VoiceHistoryItem) => {
    if (item.download_url && onAddToTimeline) onAddToTimeline({ url: item.download_url, text: item.text, duration: item.duration })
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-600 flex-1 min-h-0 flex flex-col">
      <div className="p-3 border-b border-gray-600 shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <Mic className="text-gray-400" size={16} />
          <h3 className="text-sm font-semibold text-white">Voiceover</h3>
        </div>

        <VoiceProfileCard onVoiceChange={setHasVoice} />

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={MAX_TEXT_LENGTH}
          placeholder="Type or paste the narration to speak..."
          disabled={isGenerating}
          className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white resize-none placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
        />
        <div className="flex items-center justify-end mt-1">
          <span className="text-[10px] text-gray-500 font-mono">{text.length}/{MAX_TEXT_LENGTH}</span>
        </div>
        <button
          onClick={handleGenerate}
          disabled={!text.trim() || isGenerating}
          className="w-full mt-1 px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {isGenerating ? (<><Loader2 className="animate-spin" size={14} />{statusText || 'Generating...'}</>) : (hasVoice ? '🎙 Generate in my voice' : '🎙 Generate voiceover')}
        </button>

        {errorMessage && (
          <div className="flex items-start gap-1.5 mt-2 bg-red-900/40 border border-red-600/40 rounded-lg px-3 py-2">
            <AlertCircle className="text-red-400 shrink-0 mt-0.5" size={12} />
            <p className="text-xs text-red-200">{errorMessage}</p>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {history.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Mic className="mx-auto mb-2" size={24} />
            <p className="text-xs">Write a script and generate a natural voiceover.</p>
            <p className="text-[11px] mt-1 text-gray-600">Generated narration stays here for this session.</p>
          </div>
        ) : (
          history.map((item) => (
            <div key={item.job_id} className="bg-gray-900/60 rounded-lg p-2 border border-gray-600/40">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-white truncate flex-1">{item.text}</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  {item.status === 'pending' && (<span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-600/30 text-amber-300 flex items-center gap-1"><Loader2 className="animate-spin" size={9} />generating</span>)}
                  {item.status === 'ready' && (<span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600/30 text-green-300">ready</span>)}
                  {item.status === 'failed' && (<span className="text-[10px] px-1.5 py-0.5 rounded bg-red-600/30 text-red-300">failed</span>)}
                </div>
              </div>

              {item.status === 'ready' && item.download_url && (
                <>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <audio controls preload="metadata" src={item.download_url} onLoadedMetadata={(e) => setDuration(item.job_id, (e.target as HTMLAudioElement).duration)} className="flex-1 h-8 min-w-0" />
                    <a href={item.download_url} download={`voice-${item.job_id.slice(0, 8)}.wav`} className="p-1 text-gray-400 hover:text-white transition-colors shrink-0" title="Download voiceover"><Download size={13} /></a>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <button
                      onClick={() => addToTimeline(item)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md bg-emerald-600/80 hover:bg-emerald-600 text-white text-[11px] font-medium"
                    >
                      <Plus size={11} /> Add to Voice track
                    </button>
                    <span
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/x-voice-clip', JSON.stringify({ url: item.download_url, text: item.text, duration: item.duration }))
                        e.dataTransfer.effectAllowed = 'copy'
                      }}
                      title="Drag onto the Voice track"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-600 text-gray-400 hover:text-white text-[11px] cursor-grab active:cursor-grabbing select-none"
                    >
                      <GripVertical size={11} /> drag
                    </span>
                  </div>
                </>
              )}

              {item.status === 'failed' && item.error && (<p className="text-[11px] text-red-300 mt-1">{item.error}</p>)}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
